/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { handleError } from '@SLR/shared-library';
import {
  GetAllMembersOfOrganizationRequest,
  OrganizationMembersApi,
  RemoveUserFromOrganizationRequest,
  SetOrganizationRolesForUserRequest
} from '@SLR/user-service-full-sdk';
import { useCallback, useMemo } from 'react';
import { useGetUserServiceApiConfiguration } from './user.service';

export const useOrganizationMembersApi = () => {
  const configuration = useGetUserServiceApiConfiguration();
  const apiInstance = useMemo(
    () => new OrganizationMembersApi(configuration),
    [configuration]
  );

  const setOrganizationRolesForUser = useCallback(
    (requestParameters: SetOrganizationRolesForUserRequest) =>
      apiInstance
        .setOrganizationRolesForUser(requestParameters)
        .catch(handleError),
    [apiInstance]
  );

  const removeUserFromOrganization = useCallback(
    (requestParameters: RemoveUserFromOrganizationRequest) =>
      apiInstance
        .removeUserFromOrganization(requestParameters)
        .catch(handleError),
    [apiInstance]
  );

  const getAllMembersOfOrganization = useCallback(
    (requestParameters: GetAllMembersOfOrganizationRequest) =>
      apiInstance
        .getAllMembersOfOrganization(requestParameters)
        .catch(handleError),
    [apiInstance]
  );

  const findMembershipsOfUser = useCallback(
    () => apiInstance.findMembershipsOfUser().catch(handleError),
    [apiInstance]
  );

  return {
    setOrganizationRolesForUser,
    removeUserFromOrganization,
    getAllMembersOfOrganization,
    findMembershipsOfUser
  };
};
